/**
 * Thread @-mention parser — pure core (group-threading P2b).
 *
 * The composer inserts a mention as a `<@uid>` token (uid = the member's Firebase
 * uid; mixed-case, unlike the gate's uppercase-only server regex — the client path
 * is authoritative for the P2a-driven summon). On send, `parseMentionUids` extracts
 * the summoned uids; while typing, `detectActiveMention` powers the autocomplete and
 * `insertMention` rewrites the `@query` into a token. All pure + deterministic +
 * React-free so it unit-tests in isolation (mirrors the other phases' cores).
 *
 * This layer NEVER writes membership and NEVER authorizes — it only turns text into
 * a list of uids; the server (`threadMembershipWrite` via P2a) is the sole authority.
 */

/** `<@uid>` token — uid is 1-128 chars of [A-Za-z0-9_-] (covers Firebase uids). */
const MENTION_TOKEN = /<@([A-Za-z0-9_-]{1,128})>/g;

/** Build the canonical token the composer inserts for a chosen member. */
export function mentionToken(uid: string): string {
  return `<@${(uid ?? '').trim()}>`;
}

/**
 * Extract the unique summoned uids from a message's `<@uid>` tokens, in first-seen
 * order. Blank/duplicate tokens are dropped. Never throws.
 */
export function parseMentionUids(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  if (!text) return out;
  MENTION_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_TOKEN.exec(text)) !== null) {
    const uid = (m[1] ?? '').trim();
    if (uid && !seen.has(uid)) {
      seen.add(uid);
      out.push(uid);
    }
  }
  return out;
}

/** True when a message contains at least one `<@uid>` mention token. */
export function hasMention(text: string): boolean {
  MENTION_TOKEN.lastIndex = 0;
  return !!text && MENTION_TOKEN.test(text);
}

export interface ActiveMention {
  /** The partial text typed after `@` and before the caret (may be empty). */
  query: string;
  /** Index of the `@` that opened this mention. */
  start: number;
  /** The caret position (exclusive end of the query). */
  end: number;
}

/**
 * If the caret sits inside an in-progress `@mention` (an `@` that begins a word,
 * with only mention-name characters between it and the caret), return the active
 * query for the autocomplete; otherwise `null`.
 *
 * A mention opens only when `@` is at the start of the text or preceded by
 * whitespace, and closes at the first whitespace — so `email@x.com` never triggers.
 */
export function detectActiveMention(text: string, caret: number): ActiveMention | null {
  if (!text) return null;
  const pos = Math.max(0, Math.min(caret, text.length));

  // Walk left from the caret over allowed mention-name chars to find the `@`.
  let i = pos;
  while (i > 0) {
    const ch = text[i - 1];
    if (ch === '@') {
      const before = i - 2 >= 0 ? text[i - 2] : '';
      // `@` must start a word (start-of-text or after whitespace) to open a mention.
      if (before === '' || /\s/.test(before)) {
        return { query: text.slice(i, pos), start: i - 1, end: pos };
      }
      return null;
    }
    // Allowed inside a mention query: word chars, dot, hyphen, apostrophe, space?
    // Stop at whitespace — a mention name has no spaces here (uid/handle style).
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
}

/**
 * Replace the active `@query` span with a `<@uid>` token followed by a space, and
 * return the new text + the caret position after the inserted token. Pure.
 */
export function insertMention(
  text: string,
  active: Pick<ActiveMention, 'start' | 'end'>,
  uid: string,
): { text: string; caret: number } {
  const token = `${mentionToken(uid)} `;
  const start = Math.max(0, Math.min(active.start, text.length));
  const end = Math.max(start, Math.min(active.end, text.length));
  const next = text.slice(0, start) + token + text.slice(end);
  return { text: next, caret: start + token.length };
}
